import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from 'store';
import { socketManager } from 'utils/socketManager';
import { playApi } from 'store/services/play/playApi';
import { PLAY_SOCKET_EVENTS } from 'constants/play';
import type { LiveState, PlayJudgeEvent, PlayMatchEvent, PlayPlayersEvent, PlayRoundEvent, PlayTournamentEvent, TournamentState } from 'types/interfaces/play';

interface Options {
  code: string;
  /** Mount the subscription (false until the first payload arrived, or on screens that do not need it) */
  enabled: boolean;
  /** Patch the public live cache instead of the tournament cache */
  mode?: 'tournament' | 'live';
}

const applyRound = (draft: { currentRound: TournamentState['currentRound'] }, round: PlayRoundEvent) => {
  const { tournamentId: _t, inviteCode: _c, ...state } = round;
  draft.currentRound = state;
};

const applyMatch = (draft: { currentRound: TournamentState['currentRound'] }, match: PlayMatchEvent) => {
  if (!draft.currentRound) return;
  const { tournamentId: _t, inviteCode: _c, serverTime: _s, ...state } = match;
  const idx = draft.currentRound.matches.findIndex((m) => m.id === state.id);
  if (idx >= 0) draft.currentRound.matches[idx] = state;
  const done = draft.currentRound.matches.filter((m) => m.status !== 'PENDING' || m.isBye).length;
  draft.currentRound.resultsIn = done;
};

/**
 * Per-tournament room (backend Contract delta, section C). Emits play:watch on
 * every (re)connect because the server's room membership does not survive a
 * reconnect, refetches the query on reconnect as the catch-up, and patches the
 * RTK cache for each room event inside a microtask (socket.io fires handlers
 * synchronously; a dispatch during a reducer throws Redux #3). Room events
 * never raise toasts: banners come from play:notify on the user room.
 */
export const usePlaySocket = ({ code, enabled, mode = 'tournament' }: Options): void => {
  const dispatch = useDispatch<AppDispatch>();
  const acquired = useRef(false);

  useEffect(() => {
    if (!enabled || !code) return;
    const socket = socketManager.acquireSocket();
    acquired.current = true;
    let hadConnection = socket.connected;

    const patchTournament = (fn: (draft: TournamentState) => void) => dispatch(playApi.util.updateQueryData('getTournament', code, fn));
    const patchLive = (fn: (draft: LiveState) => void) => dispatch(playApi.util.updateQueryData('getLive', code, fn));
    const refetch = () => {
      if (mode === 'live') dispatch(playApi.util.invalidateTags([{ type: 'PlayLive', id: code }]));
      else dispatch(playApi.util.invalidateTags([{ type: 'PlayTournament', id: code }]));
    };

    const watch = () => socket.emit(PLAY_SOCKET_EVENTS.watch, { code });

    const onConnect = () => {
      watch();
      if (hadConnection) queueMicrotask(refetch);
      hadConnection = true;
    };
    const onTournament = (p: PlayTournamentEvent) =>
      queueMicrotask(() => {
        if (p.inviteCode && p.inviteCode !== code) return;
        if (mode === 'live') {
          patchLive((d) => {
            d.tournament.status = p.status;
            d.tournament.registeredCount = p.registeredCount;
            d.tournament.activeCount = p.activeCount;
            d.serverTime = p.serverTime;
          });
        } else {
          patchTournament((d) => {
            d.tournament.status = p.status;
            d.tournament.registeredCount = p.registeredCount;
            d.tournament.activeCount = p.activeCount;
            d.tournament.decklistsIn = p.decklistsIn;
            d.serverTime = p.serverTime;
          });
        }
        // Status changes move the viewer between screens and change standings: fetch the full state.
        refetch();
      });
    const onRound = (p: PlayRoundEvent) =>
      queueMicrotask(() => {
        if (p.inviteCode && p.inviteCode !== code) return;
        if (mode === 'live') patchLive((d) => applyRound(d, p));
        else
          patchTournament((d) => {
            applyRound(d, p);
            d.serverTime = p.serverTime;
            if (!d.rounds.some((r) => r.id === p.id)) d.rounds.push({ id: p.id, number: p.number, phase: p.phase, startedAt: p.startedAt });
            else d.rounds = d.rounds.map((r) => (r.id === p.id ? { ...r, startedAt: p.startedAt } : r));
            if (d.myMatch) d.myMatch = p.matches.find((m) => m.id === d.myMatch?.id) ?? d.myMatch;
            if (d.viewer.role === 'PLAYER' && d.viewer.tournamentPlayerId) {
              d.myMatch = p.matches.find((m) => m.playerA.tournamentPlayerId === d.viewer.tournamentPlayerId || m.playerB?.tournamentPlayerId === d.viewer.tournamentPlayerId) ?? null;
            }
          });
      });
    const onMatch = (p: PlayMatchEvent) =>
      queueMicrotask(() => {
        if (p.inviteCode && p.inviteCode !== code) return;
        if (mode === 'live') patchLive((d) => applyMatch(d, p));
        else
          patchTournament((d) => {
            applyMatch(d, p);
            if (d.myMatch?.id === p.id) {
              const { tournamentId: _t, inviteCode: _c, serverTime: _s, ...state } = p;
              d.myMatch = state;
            }
          });
        // Results move standings; the server computes tiebreakers.
        refetch();
      });
    const onJudge = (p: PlayJudgeEvent) =>
      queueMicrotask(() => {
        if ((p.inviteCode && p.inviteCode !== code) || mode === 'live') return;
        patchTournament((d) => {
          const open = (d.judgeCalls ?? []).filter((j) => j.id !== p.judgeCall.id);
          if (!p.resolved) open.push(p.judgeCall);
          d.judgeCalls = open;
          if (d.currentRound) {
            d.currentRound.matches = d.currentRound.matches.map((m) => (m.id === p.matchId ? { ...m, openJudgeCall: p.resolved ? null : { id: p.judgeCall.id, createdAt: p.judgeCall.createdAt } } : m));
          }
          if (d.myMatch?.id === p.matchId) d.myMatch = { ...d.myMatch, openJudgeCall: p.resolved ? null : { id: p.judgeCall.id, createdAt: p.judgeCall.createdAt } };
        });
      });
    const onPlayers = (p: PlayPlayersEvent) =>
      queueMicrotask(() => {
        if ((p.inviteCode && p.inviteCode !== code) || mode === 'live') return;
        patchTournament((d) => {
          d.players = p.players;
          d.serverTime = p.serverTime;
        });
      });
    const onError = (p: { code?: string; inviteCode?: string }) => {
      if (p?.inviteCode && p.inviteCode !== code) return;
      console.warn('[PlaySocket] watch refused:', p?.code);
    };

    socket.on('connect', onConnect);
    socket.on(PLAY_SOCKET_EVENTS.tournament, onTournament);
    socket.on(PLAY_SOCKET_EVENTS.round, onRound);
    socket.on(PLAY_SOCKET_EVENTS.match, onMatch);
    socket.on(PLAY_SOCKET_EVENTS.judge, onJudge);
    socket.on(PLAY_SOCKET_EVENTS.players, onPlayers);
    socket.on(PLAY_SOCKET_EVENTS.error, onError);
    if (socket.connected) watch();

    return () => {
      socket.off('connect', onConnect);
      socket.off(PLAY_SOCKET_EVENTS.tournament, onTournament);
      socket.off(PLAY_SOCKET_EVENTS.round, onRound);
      socket.off(PLAY_SOCKET_EVENTS.match, onMatch);
      socket.off(PLAY_SOCKET_EVENTS.judge, onJudge);
      socket.off(PLAY_SOCKET_EVENTS.players, onPlayers);
      socket.off(PLAY_SOCKET_EVENTS.error, onError);
      if (socket.connected) socket.emit(PLAY_SOCKET_EVENTS.unwatch, { code });
      if (acquired.current) {
        socketManager.releaseSocket();
        acquired.current = false;
      }
    };
  }, [code, enabled, mode, dispatch]);
};

export default usePlaySocket;
